import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, endpoints } from 'utils/api/endpoints';
import {
  CreateCouponRequest,
  UpdateCouponRequest,
  CouponListResponse,
  CouponUsageResponse,
  CouponFilters,
  CouponUsageFilters,
  Coupon,
} from 'app/types/coupon';

export interface State {}

export const initialState: State = {};

const slice = createSlice({
  name: 'couponDiscounts',
  initialState,
  reducers: {},
});

export const api = createApi({
  reducerPath: 'couponDiscountsApi',
  baseQuery,
  tagTypes: ['Coupon', 'CouponUsage'],
  endpoints: builder => ({
    createCoupon: builder.mutation<any, CreateCouponRequest>({
      query: data => ({
        url: endpoints.COUPON_DISCOUNTS + '/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Coupon'],
    }),
    getCouponById: builder.query<{ data: Coupon; message: string }, string>({
      query: id => ({
        url: endpoints.COUPON_DISCOUNTS + '/' + id,
        method: 'GET',
      }),
      providesTags: ['Coupon'],
    }),
    getCouponDiscounts: builder.query<CouponListResponse, CouponFilters>({
      query: (params = {}) => ({
        url: endpoints.COUPON_DISCOUNTS + '/list',
        method: 'GET',
        params,
      }),
      providesTags: ['Coupon'],
    }),
    deleteCoupon: builder.mutation<any, number>({
      query: id => ({
        url: endpoints.COUPON_DISCOUNTS + '/' + id + '/delete',
        method: 'DELETE',
      }),
      invalidatesTags: ['Coupon'],
    }),
    updateCoupon: builder.mutation<any, UpdateCouponRequest>({
      query: ({ id, ...data }) => ({
        url: endpoints.COUPON_DISCOUNTS + '/' + id + '/update',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Coupon'],
    }),
    getCouponUsageById: builder.query<CouponUsageResponse, CouponUsageFilters>({
      query: (params = {}) => ({
        url: endpoints.COUPON_DISCOUNTS + '/' + params.coupon_id + '/usage',
        method: 'GET',
        params,
      }),
    }),
    downloadExcel: builder.mutation<any, void>({
      query: () => ({
        url: endpoints.COUPONS_DOWNLOAD_EXCEL,
        method: 'GET',
        responseHandler: 'content-type',
      }),
    }),
    getCouponUsageReport: builder.query<
      any,
      { page?: number; limit?: number; search?: string }
    >({
      query: (params = {}) => ({
        url: endpoints.COUPONS_USAGE_REPORT,
        method: 'GET',
        params,
      }),
      providesTags: ['CouponUsage'],
    }),
  }),
});

export const { actions: couponDiscountsActions } = slice;

// Export hooks for usage in functional components
export const {
  useCreateCouponMutation,
  useGetCouponDiscountsQuery,
  useDeleteCouponMutation,
  useUpdateCouponMutation,
  useGetCouponUsageByIdQuery,
  useGetCouponByIdQuery,
  useGetCouponUsageReportQuery,
} = api;

// Export the slice reducer
export const { reducer } = slice;

// Export the API slice
export const { reducer: apiReducer } = api;

// Hook for injecting the slice
export const useCouponDiscountsSlice = () => {
  const { useInjectReducer } = require('utils/redux-injectors');
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: slice.actions,
    useCreateCouponMutation,
    useGetCouponDiscountsQuery: api.useLazyGetCouponDiscountsQuery,
    useUpdateCouponMutation,
    useDeleteCouponMutation,
    useGetCouponUsageByIdQuery: api.useLazyGetCouponUsageByIdQuery,
    useGetCouponByIdQuery: api.useLazyGetCouponByIdQuery,
    useGetCouponUsageReportQuery: api.useLazyGetCouponUsageReportQuery,
  };
};
